import { useState } from "react";
import { Link } from "react-router-dom";
import Footer from "@/components/site/Footer";
import BookingSidebar from "@/components/site/BookingSidebar";
import hero from "@/assets/tour-siem-reap-oxcart.jpg";
import apsara from "@/assets/tour-apsara-relief.jpg";

const day1 = [
  { time: "05:00 AM", title: "Sunrise at Angkor Wat", body: "Watch a breathtaking sunrise over the world's largest religious monument." },
  { time: "07:30 AM", title: "Breakfast Break", body: "Pause for a leisurely breakfast near the temple grounds." },
  { time: "08:30 AM", title: "Angkor Thom", body: "South Gate, Bayon Temple (famous smiling faces), Baphuon, and the Terrace of the Elephants." },
  { time: "11:30 AM", title: "Lunch Break", body: "(not included)" },
  { time: "01:30 PM", title: "Ta Prohm", body: "The famous \"Tomb Raider\" temple with jungle roots." },
  { time: "03:00 PM", title: "Banteay Kdei", body: "A quiet and less crowded temple." },
  { time: "04:30 PM", title: "Return to Hotel", body: "Drive back to your hotel in Siem Reap." },
];

const day2 = [
  { time: "08:00 AM", title: "Hotel Pick-Up", body: "Your guide collects you from your hotel lobby." },
  { time: "09:30 AM", title: "Banteay Kdei", body: "A second, slower visit to take in the carvings without crowds." },
  { time: "11:30 AM", title: "Lunch Break", body: "(not included)" },
  { time: "01:00 PM", title: "Pre Rup", body: "Beautiful brick temple with great views." },
  { time: "02:30 PM", title: "East Mebon", body: "Royal temple guarded by stone elephants at each corner." },
  { time: "03:30 PM", title: "Ta Som", body: "A small, intimate temple swallowed by a strangler fig." },
  { time: "04:30 PM", title: "Neak Pean", body: "An island temple set in an artificial pond." },
  { time: "05:00 PM", title: "Preah Khan", body: "One of the largest and most atmospheric temples." },
  { time: "06:00 PM", title: "Return to Hotel", body: "Evening drive back into Siem Reap." },
];

const day3 = [
  { time: "08:30 AM", title: "Visit Tonlé Sap Lake", body: "Explore a floating village and learn about local life on the water. (Optional village: Kampong Phluk or Chong Khneas)" },
  { time: "12:00 PM", title: "Lunch Break", body: "Local restaurant beside the lake." },
  { time: "02:00 PM", title: "Countryside Tour", body: "Local villages, rice fields, and palm sugar production." },
  { time: "05:00 PM", title: "End of Tour", body: "Drop-off at your hotel in Siem Reap." },
];

const Timeline = ({ items }: { items: { time: string; title: string; body: string }[] }) => (
  <ol className="space-y-px">
    {items.map((s, idx) => (
      <li
        key={s.time + s.title}
        className="group grid grid-cols-[auto_1fr] md:grid-cols-[160px_1fr] gap-x-6 md:gap-x-12 border-t border-border py-8 md:py-10 transition-colors hover:bg-secondary/40 last:border-b"
      >
        <div className="flex flex-col items-start gap-y-2 text-base">
          <span className="font-display text-primary tabular-nums text-xl">
            {String(idx + 1).padStart(2, "0")}
          </span>
          <span className="uppercase tracking-[0.25em] text-primary text-xs md:text-base">
            {s.time}
          </span>
        </div>
        <div>
          <h3 className="font-display text-2xl md:text-3xl leading-tight mb-3 transition-colors group-hover:text-primary">
            {s.title}
          </h3>
          <p className="text-muted-foreground font-light leading-relaxed text-lg max-w-2xl">
            {s.body}
          </p>
        </div>
      </li>
    ))}
  </ol>
);

const days = [
  {
    key: "01",
    label: "Day One",
    title: "Angkor Small Circuit & Sunrise.",
    items: day1,
    image: apsara,
    imageAlt: "Bas-relief carving of three apsara dancers on an Angkor temple wall",
  },
  {
    key: "02",
    label: "Day Two",
    title: "Angkor Big Circuit & Hidden Temples.",
    items: day2,
    image: apsara,
    imageAlt: "Bas-relief carving of three apsara dancers on an Angkor temple wall",
  },
  {
    key: "03",
    label: "Day Three",
    title: "Culture, Nature & Floating Village.",
    items: day3,
    image: apsara,
    imageAlt: "Bas-relief carving of three apsara dancers on an Angkor temple wall",
  },
] as const;

const ItinerarySection = () => {
  const [active, setActive] = useState(0);
  const day = days[active];
  return (
    <section className="pb-24 md:pb-32">
      <div className="container max-w-6xl">
        <div className="grid lg:grid-cols-[1fr_420px] gap-x-12 gap-y-12 items-start">
          <div>
            <div className="mb-12 overflow-hidden aspect-[4/3] bg-secondary shadow-soft">
              <img
                src={day.image}
                alt={day.imageAlt}
                loading="lazy"
                className="h-full w-full object-cover"
              />
            </div>

            <div role="tablist" aria-label="Itinerary days" className="flex justify-center gap-10 md:gap-16 mb-16">
              {days.map((d, i) => {
                const isActive = i === active;
                return (
                  <button
                    key={d.key}
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => setActive(i)}
                    className={`relative pb-3 uppercase tracking-[0.3em] transition-colors text-base ${
                      isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {d.label}
                    <span
                      className={`absolute left-0 right-0 -bottom-px h-px transition-colors ${
                        isActive ? "bg-primary" : "bg-transparent"
                      }`}
                    />
                  </button>
                );
              })}
            </div>

            <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— {day.label}</p>
            <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-16 text-balance">{day.title}</h2>
            <Timeline items={day.items} />
          </div>
          <BookingSidebar pricePerPerson={315} tourTitle="Three Day Siem Reap Temple Tour" pricingTiers={[{ maxPax: 2, price: 315 }, { maxPax: 6, price: 367 }, { maxPax: 10, price: 435 }]} />
        </div>
      </div>
    </section>
  );
};

const TourSiemReapThreeDay = () => {
  return (
    <main className="bg-background">
      {/* HERO */}
      <header className="relative bg-jungle text-background overflow-hidden">
        <img
          src={hero}
          alt="Siem Reap 3 day private tour with French-speaking guide — traditional ox cart through a Khmer village"
          width={1920}
          height={1280}
          className="absolute inset-0 h-full w-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-jungle/70 via-jungle/50 to-jungle/95" />
        <div className="relative z-10">
          <div className="container flex items-center justify-between py-6">
            <Link to="/" className="flex items-center gap-2">
              <span className="font-display text-2xl tracking-wide">Aṅkor</span>
              <span className="text-[10px] uppercase tracking-[0.3em] opacity-70 hidden sm:inline">Sojourns</span>
            </Link>
            <Link to="/tours" className="text-[11px] uppercase tracking-[0.3em] border-b border-background/40 pb-1 hover:border-primary hover:text-primary transition-colors">
              ← All Tours
            </Link>
          </div>
          <div className="container pt-20 pb-32 md:pt-28 md:pb-44 max-w-5xl">
            <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Three Day Sojourn</p>
            <h1 className="font-display text-5xl md:text-7xl leading-[0.95] text-balance lg:text-2xl">
              Siem Reap Temples &amp; Local Life.
            </h1>
            <p className="mt-10 max-w-xl text-background/70 leading-relaxed text-xl font-light">
              Explore the wonders of Siem Reap over three unforgettable days, discovering the ancient temples of Angkor Archaeological Park, local culture, and the beautiful countryside. From sunrise at Angkor Wat to floating villages on Tonlé Sap Lake, every day offers something extraordinary.
            </p>
            <div className="mt-10 flex flex-wrap gap-x-10 gap-y-3 text-[11px] uppercase tracking-[0.3em] text-background/80">
              <span>Duration · 3 Days</span>
              <span>Group · 2–15</span>
              <span>From $315 / pax</span>
            </div>
          </div>
        </div>
      </header>

      {/* INTRO */}
      <section className="py-24 md:py-32">
        <div className="container max-w-4xl">
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— The Idea</p>
          <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-10 text-balance">
            Iconic highlights, hidden temples, and the rhythm of village life.
          </h2>
          <div className="space-y-6 text-lg font-light text-muted-foreground leading-relaxed">
            <p>
              This itinerary combines the must-see monuments of Angkor with the quieter corners most travellers miss small jungle temples, lakeside floating villages, and back-roads through rice fields and palm sugar farms.
            </p>
            <p>
              Each day is privately guided, paced for the heat, and built around the best light for photography and the quietest hours at each temple.
            </p>
          </div>
        </div>
      </section>

      {/* ITINERARY (TABBED) */}
      <ItinerarySection />

      {/* OPTIONAL */}
      <section className="bg-secondary py-24 md:py-32">
        <div className="container max-w-4xl">
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Optional Add-ons</p>
          <ul className="space-y-4 font-light text-lg">
            <li className="border-b border-border pb-4">Phare Cambodian Circus (evening show)</li>
            <li className="pb-4">Local market visit</li>
          </ul>
        </div>
      </section>

      {/* INCLUDES / EXCLUDES */}
      <section className="py-24 md:py-32">
        <div className="container max-w-5xl grid md:grid-cols-2 gap-x-12 gap-y-12">
          <div>
            <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Includes</p>
            <ul className="space-y-4 font-light text-lg">
              <li className="border-b border-border pb-4">English-speaking driver or guide</li>
              <li className="border-b border-border pb-4">Transportation (tuk-tuk / car / van)</li>
              <li className="pb-4">Cold water &amp; towels</li>
            </ul>
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Excludes</p>
            <ul className="space-y-4 font-light text-lg">
              <li className="border-b border-border pb-4">Angkor Pass</li>
              <li className="border-b border-border pb-4">Boat ticket for Tonlé Sap Lake</li>
              <li className="border-b border-border pb-4">Meals &amp; personal expenses</li>
              <li className="pb-4">Tips</li>
            </ul>
          </div>
        </div>
      </section>

      {/* WHAT TO BRING */}
      <section className="bg-secondary py-24 md:py-32">
        <div className="container max-w-5xl">
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Essential Items</p>
          <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-16 text-balance">What to bring, what to leave behind.</h2>
          <div className="grid md:grid-cols-3 gap-x-10 gap-y-12">
            <div>
              <h3 className="font-display text-2xl mb-5">Required</h3>
              <ul className="space-y-3 font-light text-muted-foreground">
                <li>Flashlight or phone light</li>
                <li>Temple pass (US$37)</li>
                <li>Modest clothing shoulders &amp; knees covered</li>
                <li>Comfortable walking shoes</li>
              </ul>
            </div>
            <div>
              <h3 className="font-display text-2xl mb-5">Recommended</h3>
              <ul className="space-y-3 font-light text-muted-foreground">
                <li>Camera with extra batteries</li>
                <li>Small backpack</li>
                <li>Cash for breakfast</li>
                <li>Sun protection hat, sunscreen</li>
              </ul>
            </div>
            <div>
              <h3 className="font-display text-2xl mb-5">Leave at Hotel</h3>
              <ul className="space-y-3 font-light text-muted-foreground">
                <li>Passport (bring a copy)</li>
                <li>Airline tickets</li>
                <li>Excess cash</li>
                <li>Valuable jewellery</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* GOOD TO KNOW */}
      <section className="py-24 md:py-32">
        <div className="container max-w-4xl">
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Good to Know</p>
          <div className="space-y-6 text-lg font-light text-muted-foreground leading-relaxed">
            <p><span className="text-foreground">Best time to visit.</span> November to March (cool, dry season). Early mornings are best for temples.</p>
            <p>Dress code: casual but respectful covered shoulders and knees required inside temples. Comfortable walking shoes are strongly recommended.</p>
            <p>For safety, leave valuables at your hotel and bring only what you need. This tour is not wheelchair accessible. Children under 8 are not permitted. Maximum group size: 2–15 travellers in an air-conditioned vehicle.</p>
            <p><span className="text-foreground">Weather.</span> Hot and dry throughout. There is always the possibility of a shower or two, but it is likely to be dry most of the time. Midday temperatures are likely to be very hot, but most of the woodland sites we visit have low humidity.</p>
            <p><span className="text-foreground">Food.</span> Khmer cuisine is similar to most Southeast Asian cooking, but rather less spicy. Good bread is available in many areas thanks to the French influence, and there is a surprisingly wide range of local brews to try.</p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24 md:pb-32">
        <div className="container max-w-3xl text-center">
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Reserve Your Days</p>
          <h2 className="font-display text-4xl md:text-6xl leading-[1.05] mb-10 text-balance">
            Three days. One unforgettable kingdom.
          </h2>
          <Link
            to="/#contact"
            className="inline-block bg-foreground text-background px-10 py-4 text-[11px] uppercase tracking-[0.3em] hover:bg-primary transition-colors"
          >
            Book Now →
          </Link>
        </div>
      </section>
      <Footer />
    </main>
  );
};

export default TourSiemReapThreeDay;
